import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, Loader2, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { gbp } from "../lib/format";
import {
  PageHeader, Card, Field, Input, NumberInput, Textarea,
  ToggleGroup, PrimaryButton, QuietButton
} from "../components/UI";
import { PriceHistoryPanel } from "../components/PriceHistoryPanel";

/* One tier's rate block. Lives at module scope on purpose: defining it inside
 * the page re-created the component type on every keystroke, so React
 * remounted both inputs and the cursor fell out after the first digit. */
function Tier({ tier, hpd, hourly, daily, primaryField, onHourly, onDaily, onPrimary }) {
  const isT2 = tier === 2;
  return (
    <div
      className="rounded-md border"
      style={{
        borderColor: isT2 ? "#E2DDD2" : "#D6E2D8",
        background: isT2 ? "#FAF8F2" : "#F4F8F4",
        padding: 16
      }}
      data-testid={`labour-rate-tier-${tier}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="col-head" style={{ fontSize: 9.5, color: isT2 ? "#7A6238" : "#3A5A47" }}>
            {isT2 ? "Tier 2 — Pay rate (internal only)" : "Tier 1 — Charge rate"}
          </div>
          <div className="text-ink" style={{ fontSize: 12.5, marginTop: 2, fontWeight: 500 }}>
            {isT2 ? (
              <span className="inline-flex items-center gap-1.5">
                <Lock size={11} className="text-ink-3" />
                What we actually pay the contractor. Never appears in any client-facing document.
              </span>
            ) : (
              "What we price at — feeds the composite item rate on the client Detailed Estimate (labour + materials + plant + subby per unit, O&P loaded). The client never sees this number on its own."
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Field label="Hourly rate">
          <NumberInput value={hourly} onChange={onHourly} prefix="£" suffix="/hr" data-testid={`labour-rate-tier-${tier}-hourly`} />
        </Field>
        <Field label="Day rate">
          <NumberInput value={daily} onChange={onDaily} prefix="£" suffix="/day" data-testid={`labour-rate-tier-${tier}-daily`} />
        </Field>
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          <span className="col-head" style={{ fontSize: 9.5 }}>Source of truth</span>
          <ToggleGroup
            value={primaryField}
            onChange={onPrimary}
            options={[
              { value: "hourly", label: "Hourly" },
              { value: "daily",  label: "Daily" }
            ]}
          />
        </div>
        <p className="text-ink-3" style={{ fontSize: 11 }}>
          Calculation uses Working Day = <span className="font-mono tnum">{hpd}h</span>/day.
          Editing one auto-updates the other.
        </p>
      </div>
    </div>
  );
}

export default function LabourRateEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editing = !!id;
  const [hpd, setHpd] = useState(8.0);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({
    trade_name: "", trade_code: "",
    hourly_rate: 0, day_rate: 0, primary_field: "hourly",
    actual_hourly_rate: 0, actual_day_rate: 0, actual_primary_field: "hourly",
    notes: ""
  });
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  // Load working day & doc
  useEffect(() => {
    api.get("/settings/working-day")
      .then((r) => setHpd(Number(r.data?.hours_per_day || 8.0)))
      .catch(() => {});
    if (!editing) { setLoading(false); return; }
    api.get(`/labour-rates/${id}`).then((r) => {
      setDoc(r.data);
      setForm({
        trade_name: r.data.trade_name, trade_code: r.data.trade_code || "",
        hourly_rate: r.data.hourly_rate, day_rate: r.data.day_rate,
        primary_field: r.data.primary_field,
        actual_hourly_rate: r.data.actual_hourly_rate,
        actual_day_rate: r.data.actual_day_rate,
        actual_primary_field: r.data.actual_primary_field,
        notes: r.data.notes || ""
      });
    }).catch(() => toast.error("Labour rate not found"))
      .finally(() => setLoading(false));
  }, [id, editing]);

  // Bidirectional calc
  const setHourly = useCallback((tier) => (v) => {
    const h = Number(v) || 0;
    if (tier === 1) {
      setForm((f) => ({ ...f, hourly_rate: h, day_rate: +(h * hpd).toFixed(2), primary_field: "hourly" }));
    } else {
      setForm((f) => ({ ...f, actual_hourly_rate: h, actual_day_rate: +(h * hpd).toFixed(2), actual_primary_field: "hourly" }));
    }
  }, [hpd]);

  const setDaily = useCallback((tier) => (v) => {
    const d = Number(v) || 0;
    if (tier === 1) {
      setForm((f) => ({ ...f, day_rate: d, hourly_rate: hpd ? +(d / hpd).toFixed(2) : 0, primary_field: "daily" }));
    } else {
      setForm((f) => ({ ...f, actual_day_rate: d, actual_hourly_rate: hpd ? +(d / hpd).toFixed(2) : 0, actual_primary_field: "daily" }));
    }
  }, [hpd]);

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.trade_name) { setError("Trade name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/labour-rates/${id}`, form);
        toast.success("Labour rate saved");
      } else {
        await api.post("/labour-rates", form);
        toast.success("Labour rate created");
      }
      navigate("/labour-rates");
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const archive = async () => {
    if (!editing || !window.confirm("Archive this labour rate?")) return;
    try { await api.delete(`/labour-rates/${id}`); toast.success("Archived"); navigate("/labour-rates"); }
    catch { toast.error("Archive failed"); }
  };

  if (loading) return <div className="app-container py-8 text-ink-3" style={{ fontSize: 12 }}>Loading…</div>;

  const tierProps = (tier) => tier === 2
    ? { hourly: form.actual_hourly_rate, daily: form.actual_day_rate, primaryField: form.actual_primary_field,
        onPrimary: (v) => setForm((f) => ({ ...f, actual_primary_field: v })) }
    : { hourly: form.hourly_rate, daily: form.day_rate, primaryField: form.primary_field,
        onPrimary: (v) => setForm((f) => ({ ...f, primary_field: v })) };

  return (
    <div className="app-container py-8">
      <PageHeader
        title={editing ? (doc?.trade_name || "Labour rate") : "New labour rate"}
        subtitle={editing ? doc?.trade_code : "Add a trade with both Tier 1 and Tier 2 rates."}
        breadcrumb={
          <>
            <button onClick={() => navigate("/labour-rates")} className="hover:text-ink-2">Labour Rates</button>
            <ChevronRight size={12} />
            <span className="text-ink-2">{editing ? doc?.trade_name : "New"}</span>
          </>
        }
      />

      <form onSubmit={save}>
        <Card className="p-7">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5 mb-6">
            <Field label="Trade name" required>
              <Input value={form.trade_name} onChange={set("trade_name")} placeholder="e.g. Bricklayer" />
            </Field>
            <Field label="Trade code" hint="Optional short code">
              <Input value={form.trade_code} onChange={set("trade_code")} mono placeholder="e.g. BRK" />
            </Field>
          </div>

          <div className="flex flex-col gap-4">
            <Tier tier={1} hpd={hpd} onHourly={setHourly(1)} onDaily={setDaily(1)} {...tierProps(1)} />
            <Tier tier={2} hpd={hpd} onHourly={setHourly(2)} onDaily={setDaily(2)} {...tierProps(2)} />
          </div>

          <div className="mt-6">
            <Field label="Notes">
              <Textarea value={form.notes} onChange={set("notes")} placeholder="Free-form notes about this trade…" />
            </Field>
          </div>

          {error && (
            <div className="mt-5 px-3 py-2 rounded-md" style={{ background: "#FEF3E2", color: "#7A4C0A", fontSize: 12 }}>{error}</div>
          )}

          <div className="mt-7 flex items-center justify-between">
            {editing ? (
              <button type="button" onClick={archive} className="text-note-red hover:underline inline-flex items-center gap-1.5" style={{ fontSize: 12 }}>
                <Trash2 size={12} /> Archive labour rate
              </button>
            ) : <span />}
            <div className="flex items-center gap-2">
              <QuietButton onClick={() => navigate("/labour-rates")} icon={ChevronLeft}>Cancel</QuietButton>
              <PrimaryButton type="submit" disabled={saving} icon={saving ? Loader2 : Save}>
                {editing ? "Save changes" : "Create labour rate"}
              </PrimaryButton>
            </div>
          </div>
        </Card>
      </form>

      {/* LT-28 — canonical append-only ledger (Tier 1 primary field). */}
      {editing && doc?.id && (
        <div className="mt-6">
          <PriceHistoryPanel
            entityType="labour_rate"
            entityId={doc.id}
            fieldLabel="hourly rate"
          />
        </div>
      )}
    </div>
  );
}
